import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useHotelStaticByJoyPlaceQuery } from '../../routes/HotelPDP3/HotelPDP3.graphql.generated';
import { DEFAULT_HOTEL_IMAGE, JOY_BRAND_NAME } from '@shared/utils/constants';
import { addRendition } from '@shared/utils/photoRendition';

const MAX_OG_DESCRIPTION_LENGTH = 200;

/**
 * Builds a short location string from the hotel address, e.g. "New York, NY" or "Paris, France".
 */
const buildLocationString = (address: { city?: string | null; state?: string | null; country?: string | null } | null | undefined): string | undefined => {
  if (!address) return undefined;
  const parts = [address.city, address.state ?? address.country].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : undefined;
};

/**
 * Builds the canonical URL for a stays PDP, preserving the eventId query param
 * so the URL round-trips correctly when crawlers follow it.
 */
const buildCanonicalUrl = (joyPlaceId: string, eventId: string | null): string => {
  const base = `https://withjoy.com/stays/${joyPlaceId}`;
  return eventId ? `${base}?eventId=${eventId}` : base;
};

type StaysPdpHeadProps = Readonly<{
  joyPlaceId: string;
  eventId: string | null;
}>;

/**
 * SSR-safe head component for /stays/:joyPlaceId.
 *
 * Fetches static hotel catalog data during SSR (ssr: true) and renders Open Graph
 * + Twitter Card meta tags via react-helmet-async so link preview crawlers
 * (Slack, iMessage, WhatsApp, etc.) can display a rich unfurl.
 *
 * The rest of the PDP body stays client-only (NoSsr / EventHandleFetcher),
 * matching the GuestSiteHeadCsr pattern used by guest site pages.
 */
export const StaysPdpHead: React.FC<StaysPdpHeadProps> = ({ joyPlaceId, eventId }) => {
  const { data } = useHotelStaticByJoyPlaceQuery({
    variables: { idObject: { joyPlaceId } },
    batchMode: 'fast',
    fetchPolicy: 'cache-first',
    ssr: true,
  });

  const property = data?.accommodationCatalogPropertyById;

  const ogData = useMemo(() => {
    if (!property) return null;

    const hotelName = property.name || 'Hotel';
    const location = buildLocationString(property.address);
    const title = location ? `${hotelName} – ${location}` : hotelName;

    let description = property.plainTextLongDescription;
    if (description && description.length > MAX_OG_DESCRIPTION_LENGTH) {
      description = `${description.slice(0, MAX_OG_DESCRIPTION_LENGTH)}…`;
    }

    const imageUrl = property.photos?.[0]?.url ? (addRendition({ url: property.photos[0].url, renditionSize: 'medium' }) as string) : DEFAULT_HOTEL_IMAGE;

    const ogUrl = buildCanonicalUrl(joyPlaceId, eventId);

    return { title, description, imageUrl, ogUrl };
  }, [property, joyPlaceId, eventId]);

  if (!ogData) {
    return null;
  }

  return (
    <Helmet title={`${ogData.title} | ${JOY_BRAND_NAME}`}>
      <meta
        property="og:title"
        content={ogData.title}
      />
      <meta
        property="og:site_name"
        content={JOY_BRAND_NAME}
      />
      {ogData.description && (
        <meta
          property="og:description"
          content={ogData.description}
        />
      )}
      <meta
        property="og:url"
        content={ogData.ogUrl}
      />
      <meta
        property="og:image"
        content={ogData.imageUrl}
      />
      <meta
        property="og:type"
        content="website"
      />
      <meta
        name="twitter:card"
        content="summary_large_image"
      />
      <meta
        name="twitter:title"
        content={ogData.title}
      />
      {ogData.description && (
        <meta
          name="twitter:description"
          content={ogData.description}
        />
      )}
      <meta
        name="twitter:image"
        content={ogData.imageUrl}
      />
      <meta
        name="twitter:site"
        content="@JoytheApp"
      />
    </Helmet>
  );
};
